import { useEffect, useRef, useState } from 'react';
import { Send, RotateCcw } from 'lucide-react';
import { streamAssistant } from '../assistant/streamAssistant';
import RichText from '../assistant/RichText';

const API = process.env.REACT_APP_BACKEND_URL;
const KEY = 'ff_admin_ai_session';
const PROMPTS = ['Give me a library health summary', 'List 10 items missing titles', 'Run the beta tests and summarise failures', 'What did you change today?'];

export default function AdminAiChat({ onToolActivity }) {
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(KEY));
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (!sessionId) return;
    fetch(`${API}/api/admin-ai/history/${sessionId}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { messages: [] }))
      .then((d) => setMessages(d.messages || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput(''); setSending(true);
    setMessages((m) => [...m, { role: 'user', content: msg }, { role: 'assistant', content: '' }]);
    const append = (chunk) => setMessages((m) => {
      const c = [...m];
      c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + chunk };
      return c;
    });
    await streamAssistant(msg, sessionId, {
      onSession: (id) => { localStorage.setItem(KEY, id); setSessionId(id); },
      onDelta: append, onError: append,
    }, '/api/admin-ai/chat');
    setSending(false);
    onToolActivity();
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl flex flex-col h-[560px]" data-testid="admin-ai-chat">
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
        <div className="font-bold text-white text-sm">Admin AI <span className="text-zinc-500 font-normal">· library + beta testing</span></div>
        <button onClick={() => { localStorage.removeItem(KEY); setSessionId(null); setMessages([]); }} className="p-2 rounded-full hover:bg-zinc-800 text-zinc-400" data-testid="admin-ai-new-chat-btn"><RotateCcw className="w-4 h-4" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 text-sm" data-testid="admin-ai-messages">
        {messages.length === 0 && PROMPTS.map((p, i) => (
          <button key={p} onClick={() => send(p)} className="block w-full text-left px-3 py-2 rounded-lg border border-zinc-800 text-zinc-300 hover:border-[#00D26A] transition-colors" data-testid={`admin-ai-prompt-${i}`}>{p}</button>
        ))}
        {messages.map((m, i) => (
          <div key={i} className={`whitespace-pre-wrap rounded-xl px-3 py-2 ${m.role === 'user' ? 'ml-auto max-w-[85%] bg-[#00D26A] text-black' : 'bg-black text-zinc-200'}`} data-testid={`admin-ai-msg-${m.role}-${i}`}>
            {m.content ? <RichText text={m.content} testid={`admin-ai-msg-${i}`} /> : (sending ? 'Working…' : '')}
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 p-3 border-t border-zinc-800">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask the Admin AI to check, fix or test…" className="flex-1 bg-black border border-zinc-800 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-[#00D26A]" data-testid="admin-ai-input" />
        <button type="submit" disabled={sending || !input.trim()} className="p-2.5 rounded-full disabled:opacity-40" style={{ background: '#00D26A', color: '#050505' }} data-testid="admin-ai-send-btn"><Send className="w-4 h-4" /></button>
      </form>
    </div>
  );
}
